import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Badge,
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Input,
    Row,
    Spinner
} from "reactstrap";
import classNames from "classnames";

import TableContainerReactTable from "../../Components/Common/TableContainerReactTable";
import {
    DashboardPageHeader,
    ResponsiveTabs,
} from "../../Components/Common/DashboardKit";
import api from "../../api";
import { useNavigate } from "react-router-dom";

/**
 * Task Management dashboard.
 *
 * Pass 5 UI update (2026):
 *  - Replaced the inline Nav / NavItem / NavLink tab strip with the
 *    shared ResponsiveTabs from the DashboardKit. The tab definitions
 *    in MY_TASK_TABS are unchanged; we just project them through
 *    ResponsiveTabs's { key, label, count } shape.
 *  - Tab badges (the per-tab count of returned rows) are now
 *    rendered by ResponsiveTabs itself, with consistent active/inactive
 *    styling. The original conditional that hid empty counts is
 *    preserved by only setting `count` on the active tab — same data
 *    behavior (the API only returns one tab at a time).
 *  - classNames is still imported because the due-date column uses
 *    it for "overdue"/"due today" coloring.
 *  - Nothing else changed: fetchTasks, mapTaskRow, the column
 *    definitions, isOverdue / isDueToday / formatDate / formatTime
 *    helpers, the filteredTasks search filter, tabCounts memo,
 *    handleNewTask / handleRefresh / handleTaskClick handlers, the
 *    Refresh/New Task buttons, the API endpoint
 *    (/TaskManagement/my-tasks?tab=...&empId=...), and the
 *    TableContainerReactTable child are all unchanged.
 */
const MY_TASK_TABS = [
    { key: "unstarted", label: "Unstarted", apiValue: "unstarted" },
    { key: "wip", label: "WIP", apiValue: "wip" },
    { key: "completed", label: "Completed", apiValue: "completed" },
    { key: "rejected", label: "Rejected", apiValue: "rejected" },
    { key: "yesterday", label: "Yesterday's Activity", apiValue: "yesterday" },
    { key: "today", label: "Today's Activity", apiValue: "today" },
    { key: "assignedbyme", label: "Assigned by Me", apiValue: "assignedbyme" }
];

const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "";

    return date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric"
    });
};

const formatTime = (minutes) => {
    const safeMinutes = Number(minutes || 0);
    if (safeMinutes <= 0) return "0 m";

    const hrs = Math.floor(safeMinutes / 60);
    const mins = safeMinutes % 60;

    if (hrs > 0 && mins > 0) return `${hrs} h ${mins} m`;
    if (hrs > 0) return `${hrs} h`;
    return `${mins} m`;
};

const isOverdue = (dateString) => {
    if (!dateString) return false;

    const today = new Date();
    const due = new Date(dateString);

    if (Number.isNaN(due.getTime())) return false;

    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);

    return due < today;
};

const isDueToday = (dateString) => {
    if (!dateString) return false;

    const today = new Date();
    const due = new Date(dateString);

    if (Number.isNaN(due.getTime())) return false;

    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);

    return due.getTime() === today.getTime();
};

const mapTaskRow = (task) => {
    return {
        taskId: task.taskId ?? task.TaskId ?? task.taskID ?? task.TaskID ?? 0,
        dueDate: task.dueDate ?? task.DueDate ?? null,
        assignedByName:
            task.assignedByName ??
            task.AssignedByName ??
            task.assignedByStr ??
            task.AssignedByStr ??
            "",
        assignToName:
            task.assignToName ??
            task.AssignToName ??
            task.assignToNameStr ??
            task.AssignToNameStr ??
            "",
        description: task.description ?? task.Description ?? "",
        createdOn:
            task.createdOn ??
            task.CreatedOn ??
            task.creationDate ??
            task.CreationDate ??
            null,
        activitiesLogged:
            task.activitiesLogged ??
            task.ActivitiesLogged ??
            task.totalLaborEntries ??
            task.TotalLaborEntries ??
            0,
        totalTimeMinutes:
            task.totalTimeMinutes ??
            task.TotalTimeMinutes ??
            task.totalLaborMinutes ??
            task.TotalLaborMinutes ??
            0,
        status: task.status ?? task.Status ?? ""
    };
};

const TaskManagement = () => {
    document.title = "Task Management || SSW Technologies Dashboard";
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("unstarted");
    const [searchTerm, setSearchTerm] = useState("");
    const [taskData, setTaskData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const userId = JSON.parse(sessionStorage.getItem("authUser"))?.uid;

    // Round TM-4 (2026) — legacy parity: the menu badge count and the
    // "currently working on..." open-session banner (legacy My Tasks
    // auto-selected the In-Progress tab when a session was open).
    const [openTaskCount, setOpenTaskCount] = useState(null);
    const [openSession, setOpenSession] = useState(null);
    const [autoTabDone, setAutoTabDone] = useState(false);
    const [findTaskId, setFindTaskId] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [countRes, sessionRes] = await Promise.all([
                    api.get("/TaskManagement/my-task-count"),
                    api.get("/TaskManagement/open-session"),
                ]);
                if (cancelled) return;
                const countData = countRes?.data ?? countRes;
                const sessData = sessionRes?.data ?? sessionRes;
                setOpenTaskCount(
                    typeof countData?.count === "number" ? countData.count : null
                );
                if (sessData?.open && sessData?.session) {
                    setOpenSession(sessData.session);
                    setAutoTabDone((done) => {
                        if (!done) setActiveTab("wip");
                        return true;
                    });
                } else {
                    setOpenSession(null);
                }
            } catch {
                /* badge/banner are cosmetic — ignore failures */
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const handleFindTask = () => {
        const id = parseInt(findTaskId, 10);
        if (!Number.isNaN(id) && id > 0)
            navigate(`/task-management/view-task/${id}`);
    };

    const fetchTasks = useCallback(async (tabKey) => {
        try {

            setLoading(true);
            setError("");

            const selectedTab = MY_TASK_TABS.find((t) => t.key === tabKey) || MY_TASK_TABS[0];

            const response = await api.get("/TaskManagement/my-tasks", {
                params: {
                    tab: selectedTab.apiValue,
                    empId: userId
                }
            });

            const payload = response?.data ?? response;

            const rows = Array.isArray(payload)
                ? payload
                : Array.isArray(payload?.items)
                    ? payload.items
                    : Array.isArray(payload?.data)
                        ? payload.data
                        : [];
            setTaskData(rows.map(mapTaskRow));
        } catch (err) {
            console.error("Task fetch error:", err);
            setTaskData([]);
            setError(err?.response?.data || err?.message || "Unable to load tasks.");
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        if (!userId) {
            setError("Employee ID not found in session.");
            return;
        }

        fetchTasks(activeTab);
    }, [activeTab, userId, fetchTasks]);

    const handleTabClick = (tabKey) => {
        setActiveTab(tabKey);
        setSearchTerm("");
    };

    const handleRefresh = () => {
        fetchTasks(activeTab);
    };

    const handleNewTask = () => {
        navigate("/task-management/create");
    };

    const handleTaskClick = (task) => {
        navigate(`/task-management/view-task/${task.taskId}`);
    };

    // Since API returns only one tab at a time, only active-tab count is reliable.
    const tabCounts = useMemo(() => {
        return MY_TASK_TABS.reduce((acc, tab) => {
            acc[tab.key] = tab.key === activeTab ? taskData.length : "";
            return acc;
        }, {});
    }, [taskData, activeTab]);

    // Reshape MY_TASK_TABS for ResponsiveTabs. Only the active tab carries
    // a count (matching the original conditional `{tabCounts[tab.key] !== "" && <Badge>}`).
    const kitTabs = useMemo(() => MY_TASK_TABS.map((tab) => {
        const c = tabCounts[tab.key];
        return {
            key: tab.key,
            label: tab.label,
            count: typeof c === "number" ? c : undefined,
        };
    }), [tabCounts]);

    const filteredTasks = useMemo(() => {
        let rows = [...taskData];

        if (searchTerm.trim()) {
            const q = searchTerm.toLowerCase();
            rows = rows.filter(
                (task) =>
                    String(task.taskId).toLowerCase().includes(q) ||
                    task.description?.toLowerCase().includes(q) ||
                    task.assignedByName?.toLowerCase().includes(q)
            );
        }

        return rows;
    }, [taskData, searchTerm]);

    const columns = useMemo(
        () => [
            {
                Header: "Due Date",
                accessor: "dueDate",
                Cell: ({ row }) => {
                    const dueDate = row.original.dueDate;
                    const overdue = isOverdue(dueDate);
                    const dueToday = isDueToday(dueDate);

                    return (
                        <span
                            className={classNames("fw-semibold", {
                                "text-danger": overdue,
                                "text-warning": !overdue && dueToday
                            })}
                        >
                            {formatDate(dueDate)}
                        </span>
                    );
                }
            },
            {
                Header: "Assigned By",
                accessor: "assignedByName",
                Cell: ({ row }) => <span>{row.original.assignedByName || "-"}</span>
            },
            {
                Header: "Assigned To",
                accessor: "assignToName",
                Cell: ({ row }) => <span>{row.original.assignToName || row.original.assignTo || "-"}</span>
            },
            {
                Header: "Description",
                accessor: "description",
                Cell: ({ value }) => (
                    <div
                        className="text-truncate"
                        style={{ maxWidth: "460px" }}
                        title={value}
                    >
                        {value}
                    </div>
                )
            },
            {
                Header: "Created On",
                accessor: "createdOn",
                Cell: ({ value }) => <span>{formatDate(value)}</span>
            },
            {
                Header: "Task Id",
                accessor: "taskId",
                Cell: ({ row, value }) => (
                    <button
                        type="button"
                        className="btn btn-link p-0 text-decoration-none fw-semibold"
                        onClick={() => handleTaskClick(row.original)}
                    >
                        {value}
                    </button>
                )
            },
            {
                Header: "Status",
                accessor: "status",
                Cell: ({ value }) => (
                    <div>
                        <Badge
                            color={
                                value?.toUpperCase() === "COMPLETED"
                                    ? "success"
                                    : value?.toUpperCase() === "REJECTED"
                                        ? "danger"
                                        : value?.toUpperCase() === "ACTION_REQUIRED"
                                            ? "warning"
                                            : "secondary"
                            }
                            pill
                            className="fs-8 px-2 py-1"
                        >
                            {value}
                        </Badge>
                    </div>
                )
            },
            {
                Header: "Total Time",
                accessor: "totalTimeMinutes",
                Cell: ({ value }) => (
                    <div>{formatTime(value)}</div>
                )
            }
        ],
        []
    );

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <DashboardPageHeader
                        icon="ri-clipboard-line"
                        title="Task Management"
                        subtitle="Plan, assign, and monitor team tasks"
                        tone="default"
                        breadcrumb={[
                            { label: "Dashboards", to: "/" },
                            { label: "Task Management" },
                        ]}
                    />

                    {/* Round TM-4 (2026) — legacy Tasks menu, rebuilt as an
                        action area. Manager/auditor pages are role-gated by
                        their routes; every employee gets By Date and End of
                        Day (legacy parity). */}
                    <Row className="mb-3">
                        <Col lg={12}>
                            <Card className="shadow-sm border-0">
                                <CardBody className="d-flex flex-wrap align-items-center gap-2 py-2">
                                    {typeof openTaskCount === "number" && (
                                        <Badge color="info" pill className="fs-6 me-1">
                                            {openTaskCount} open
                                        </Badge>
                                    )}
                                    <Button color="light" size="sm" onClick={() => navigate("/task-management/dept")}>
                                        <i className="ri-team-line align-bottom me-1"></i>Dept Tasks
                                    </Button>
                                    <Button color="light" size="sm" onClick={() => navigate("/task-management/activity")}>
                                        <i className="ri-pulse-line align-bottom me-1"></i>Current Activity
                                    </Button>
                                    <Button color="light" size="sm" onClick={() => navigate("/task-management/by-date")}>
                                        <i className="ri-calendar-line align-bottom me-1"></i>By Date
                                    </Button>
                                    <Button color="light" size="sm" onClick={() => navigate("/task-management/assigned")}>
                                        <i className="ri-send-plane-line align-bottom me-1"></i>Tasks I've Assigned
                                    </Button>
                                    <Button color="light" size="sm" onClick={() => navigate("/task-management/eod")}>
                                        <i className="ri-time-line align-bottom me-1"></i>End of Day
                                    </Button>
                                    <Button color="light" size="sm" onClick={() => navigate("/task-management/auditor")}>
                                        <i className="ri-search-eye-line align-bottom me-1"></i>Task Auditor
                                    </Button>
                                    <div className="d-flex align-items-center gap-1 ms-auto">
                                        <Input
                                            bsSize="sm"
                                            type="number"
                                            placeholder="Task #"
                                            style={{ width: "110px" }}
                                            value={findTaskId}
                                            onChange={(e) => setFindTaskId(e.target.value)}
                                            onKeyDown={(e) => e.key === "Enter" && handleFindTask()}
                                        />
                                        <Button color="secondary" size="sm" onClick={handleFindTask}>
                                            Open
                                        </Button>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {openSession && (
                        <Alert color="warning" className="d-flex align-items-center justify-content-between">
                            <span>
                                <i className="ri-timer-flash-line align-bottom me-1"></i>
                                You are currently logging time on{" "}
                                <b>Task #{openSession.taskId}</b>
                                {openSession.description ? ` — ${openSession.description}` : ""}
                                {" "}(started {new Date(openSession.startTimeUserStated).toLocaleTimeString()}).
                            </span>
                            <Button
                                size="sm"
                                color="warning"
                                onClick={() => navigate(`/task-management/view-task/${openSession.taskId}`)}
                            >
                                Open Task
                            </Button>
                        </Alert>
                    )}

                    <Row>
                        <Col lg={12}>
                            <Card className="shadow-sm border-0">
                                <CardHeader className="bg-white border-0 pb-0">
                                    <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-4">
                                        <div>
                                            <h4 className="mb-1 fw-semibold">My Tasks</h4>
                                            <p className="text-muted mb-0">Track work assigned to you</p>
                                        </div>

                                        <div className="d-flex gap-2">
                                            <Button
                                                color="light"
                                                onClick={handleRefresh}
                                                disabled={loading}
                                                className="d-flex align-items-center"
                                            >
                                                {loading ? (
                                                    <Spinner size="sm" className="me-2" />
                                                ) : (
                                                    <i className="ri-refresh-line me-1" />
                                                )}
                                                Refresh
                                            </Button>

                                            <Button color="primary" onClick={handleNewTask}>
                                                <i className="ri-add-line me-1" />
                                                New Task
                                            </Button>
                                        </div>
                                    </div>

                                    <div className="task-tabs-wrapper mb-3">
                                        <ResponsiveTabs
                                            tabs={kitTabs}
                                            activeKey={activeTab}
                                            onChange={handleTabClick}
                                            variant="underline"
                                            color="primary"
                                        />
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                                        <div style={{ width: "100%", maxWidth: "520px" }}>
                                            <Input
                                                type="text"
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                placeholder="Search by Task Id, Description, or Assigned By"
                                            />
                                        </div>

                                        <div className="text-muted small">
                                            Showing <span className="fw-semibold">{filteredTasks.length}</span> task
                                            {filteredTasks.length !== 1 ? "s" : ""}
                                        </div>
                                    </div>
                                </CardHeader>

                                <CardBody className="pt-0">
                                    {error && (
                                        <Alert color="danger" className="mb-3">
                                            {error}
                                        </Alert>
                                    )}

                                    <div className="task-table-wrapper" style={{ minHeight: "420px" }}>
                                        <TableContainerReactTable
                                            key={`${activeTab}-${filteredTasks.length}-${filteredTasks[0]?.taskId || "empty"}`}
                                            columns={columns}
                                            data={filteredTasks}
                                            customPageSize={10}
                                            divClass="table-responsive table-card mb-0"
                                            tableClass="table table-centered align-middle table-nowrap mb-0 task-table"
                                            theadClass="table-light"
                                            isGlobalFilter={false}
                                            isPagination={true}
                                            SearchPlaceholder="Search..."
                                        />
                                    </div>

                                    {!loading && !error && filteredTasks.length === 0 && (
                                        <div className="text-center py-5">
                                            <div className="avatar-sm mx-auto mb-3">
                                                <div className="avatar-title bg-light text-muted rounded-circle fs-24">
                                                    <i className="ri-task-line"></i>
                                                </div>
                                            </div>
                                            <h5 className="mb-1">No tasks found</h5>
                                            <p className="text-muted mb-0">
                                                There are no tasks in this view for the current search.
                                            </p>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default TaskManagement;
